import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Briefcase, Building2, Check, Loader2, Plus, ArrowLeft, Globe, Mail, Hash, Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const BUSINESS_SIZES = ["1-10", "11-50", "51-200", "200+"];

/**
 * ProfileHub — CartBlancheJA Phase B2 #11.
 *
 * Every account starts as a Client. They can opt-in to a Freelancer
 * profile and/or a Business profile, then toggle between active roles.
 *
 * Layout:
 *   1. Header strip with active-role toggle (visible only if multi-role)
 *   2. Three role cards: Client / Freelancer / Business
 *      • Client card is always "active" (everyone is a client by default)
 *      • Freelancer + Business show "Set up" if not enabled, "Edit" if enabled
 *   3. Tapping a card opens that role's form inline
 */
export default function ProfileHub() {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRole, setOpenRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/profile/me/hub");
      setHub(res.data);
    } catch {
      toast.error("Please log in to manage your profile");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const switchRole = async (role) => {
    try {
      await api.put("/profile/active-role", { active_role: role });
      toast.success(`Switched to ${role} mode`);
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't switch role"));
    }
  };

  const roles = [
    {
      id: "client",
      icon: User,
      title: "Client",
      tag: hub?.profile?.client?.tagline || "Hire talent — post any job",
      active: true,
      cta: hub?.profile?.client?.tagline ? "Edit profile" : "Set up profile",
    },
    {
      id: "freelancer",
      icon: Briefcase,
      title: "Freelancer",
      tag: hub?.has_freelancer ? (hub?.profile?.freelancer?.tagline || "Freelancer mode active") : "Earn by bidding on jobs",
      active: hub?.has_freelancer,
      cta: hub?.has_freelancer ? "Edit profile" : "Upgrade to Freelancer",
    },
    {
      id: "business",
      icon: Building2,
      title: "Business",
      tag: hub?.has_business ? (hub?.profile?.business?.company_name || "Business mode active") : "Get verified, hire at scale",
      active: hub?.has_business,
      cta: hub?.has_business ? "Edit profile" : "Upgrade to Business",
    },
  ];

  if (loading) {
    return <div className="text-center py-12 text-white/40"><Loader2 size={20} className="animate-spin inline" /></div>;
  }

  return (
    <div data-testid="profile-hub" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Profile Hub</p>
          <h2 className="font-heading text-3xl md:text-4xl text-white">One account. Multiple roles.</h2>
          <p className="text-white/50 text-sm mt-1 max-w-xl">Every account is a Client by default. Upgrade to Freelancer to bid on jobs, or to Business for verified hiring at scale.</p>
        </div>
        {hub && (hub.has_freelancer || hub.has_business) && (
          <div data-testid="active-role-toggle" className="flex items-center gap-1 px-2 py-1 bg-black/40 border border-white/10 rounded-full text-[11px]">
            <span className="text-white/40 px-2">Active:</span>
            {["client", "freelancer", "business"].filter((r) => r === "client" || (r === "freelancer" && hub.has_freelancer) || (r === "business" && hub.has_business)).map((r) => (
              <button key={r} data-testid={`active-role-${r}`} onClick={() => switchRole(r)}
                className={`px-3 py-1 rounded-full uppercase tracking-wider transition-colors ${
                  hub.active_role === r ? "bg-gold text-luxury-black" : "text-white/60 hover:text-white"
                }`}>
                {r}
              </button>
            ))}
          </div>
        )}
      </div>

      {openRole === null ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roles.map((r, i) => (
            <motion.button
              key={r.id}
              data-testid={`role-card-${r.id}`}
              onClick={() => setOpenRole(r.id)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -2 }}
              className={`text-left p-6 border rounded-md transition-colors ${
                r.active ? "border-gold/30 bg-gradient-to-br from-gold/[0.04] to-transparent" : "border-white/10 bg-black/30 hover:border-gold/30"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <r.icon size={20} className={r.active ? "text-gold" : "text-white/40"} strokeWidth={1.5} />
                {r.active && (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                    <Check size={9} /> Active
                  </span>
                )}
              </div>
              <h3 className="font-heading text-lg text-white mb-1">{r.title}</h3>
              <p className="text-xs text-white/55 mb-4 line-clamp-2 min-h-[32px]">{r.tag}</p>
              <span className="text-[10px] uppercase tracking-wider text-gold">{r.cta} →</span>
            </motion.button>
          ))}
        </div>
      ) : (
        <div>
          <button onClick={() => setOpenRole(null)}
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-3">
            <ArrowLeft size={14} /> Back to hub
          </button>
          {openRole === "client" && (
            <ClientForm hub={hub} onSaved={() => { load(); setOpenRole(null); }} saving={saving} setSaving={setSaving} />
          )}
          {openRole === "freelancer" && (
            <FreelancerForm hub={hub} onSaved={() => { load(); setOpenRole(null); }} saving={saving} setSaving={setSaving} />
          )}
          {openRole === "business" && (
            <BusinessForm hub={hub} onSaved={() => { load(); setOpenRole(null); }} saving={saving} setSaving={setSaving} />
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================
// FORMS
// ==========================================================

function FormShell({ title, subtitle, children, onSubmit, saving, testid }) {
  return (
    <form data-testid={testid} onSubmit={onSubmit}
      className="p-6 border border-gold/20 bg-black/30 rounded-md space-y-4 max-w-2xl">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-1">{title}</p>
        <h3 className="font-heading text-xl text-white">{subtitle}</h3>
      </div>
      {children}
      <button type="submit" disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 py-3 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        Save profile
      </button>
    </form>
  );
}

function fieldClass() {
  return "w-full bg-black/40 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none";
}

function ClientForm({ hub, onSaved, saving, setSaving }) {
  const init = hub?.profile?.client || {};
  const [form, setForm] = useState({
    tagline: init.tagline || "",
    country: init.country || "",
    avatar_url: init.avatar_url || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile/client", form);
      toast.success("Client profile saved");
      onSaved();
    } catch (err) { toast.error(errorMessage(err, "Save failed")); } finally { setSaving(false); }
  };
  return (
    <FormShell title="Client" subtitle="How freelancers see you" onSubmit={submit} saving={saving} testid="client-form">
      <input data-testid="client-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
        placeholder="What you're hiring for (optional, e.g. 'I hire React devs for SaaS')" maxLength={140}
        className={fieldClass()} />
      <input data-testid="client-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
        placeholder="Country (e.g. Jamaica)" maxLength={80} className={fieldClass()} />
      <input data-testid="client-avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
        placeholder="Avatar URL (optional)" className={fieldClass()} />
    </FormShell>
  );
}

function FreelancerForm({ hub, onSaved, saving, setSaving }) {
  const init = hub?.profile?.freelancer || {};
  const [form, setForm] = useState({
    tagline: init.tagline || "",
    skills: (init.skills || []).join(", "),
    hourly_rate_jmd: init.hourly_rate_jmd || 0,
    portfolio_links: (init.portfolio_links || []).join(", "),
    years_experience: init.years_experience || 0,
    languages: (init.languages || []).join(", "),
    avatar_url: init.avatar_url || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile/freelancer", {
        tagline: form.tagline,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        hourly_rate_jmd: Number(form.hourly_rate_jmd) || 0,
        portfolio_links: form.portfolio_links.split(",").map((s) => s.trim()).filter(Boolean),
        years_experience: Number(form.years_experience) || 0,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        avatar_url: form.avatar_url || null,
      });
      toast.success("Freelancer profile saved");
      onSaved();
    } catch (err) { toast.error(errorMessage(err, "Save failed")); } finally { setSaving(false); }
  };
  return (
    <FormShell title="Freelancer" subtitle="Get hired" onSubmit={submit} saving={saving} testid="freelancer-form">
      <input data-testid="freelancer-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
        placeholder="Tagline (e.g. 'Senior React dev — production sites in 1 week')" maxLength={140} className={fieldClass()} />
      <input data-testid="freelancer-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
        placeholder="Skills (comma-separated: react, fastapi, mongo)" className={fieldClass()} />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="freelancer-rate" type="number" value={form.hourly_rate_jmd} onChange={(e) => setForm({ ...form, hourly_rate_jmd: e.target.value })}
          placeholder="Hourly rate JMD" className={fieldClass()} />
        <input data-testid="freelancer-years" type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
          placeholder="Years of experience" className={fieldClass()} />
      </div>
      <input data-testid="freelancer-portfolio" value={form.portfolio_links} onChange={(e) => setForm({ ...form, portfolio_links: e.target.value })}
        placeholder="Portfolio links (comma-separated)" className={fieldClass()} />
      <input data-testid="freelancer-languages" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })}
        placeholder="Languages (e.g. English, Patois, Spanish)" className={fieldClass()} />
      <input data-testid="freelancer-avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
        placeholder="Avatar URL (optional)" className={fieldClass()} />
    </FormShell>
  );
}

function BusinessForm({ hub, onSaved, saving, setSaving }) {
  const init = hub?.profile?.business || {};
  const [form, setForm] = useState({
    company_name: init.company_name || "",
    logo_url: init.logo_url || "",
    industry: init.industry || "",
    size: init.size || "1-10",
    website: init.website || "",
    contact_email: init.contact_email || "",
    country: init.country || "",
    registration_number: init.registration_number || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await api.put("/profile/business", form);
      toast.success("Business profile saved");
      onSaved();
    } catch (err) { toast.error(errorMessage(err, "Save failed")); } finally { setSaving(false); }
  };
  return (
    <FormShell title="Business" subtitle="Verified hiring at scale" onSubmit={submit} saving={saving} testid="business-form">
      <input data-testid="biz-name" value={form.company_name} required onChange={(e) => setForm({ ...form, company_name: e.target.value })}
        placeholder="Company name *" maxLength={160} className={fieldClass()} />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="biz-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
          placeholder="Industry (e.g. FinTech)" className={fieldClass()} />
        <select data-testid="biz-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
          className={fieldClass()}>
          {BUSINESS_SIZES.map((s) => <option key={s} value={s}>Size: {s}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="biz-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="Website" className={fieldClass()} />
        <input data-testid="biz-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          placeholder="Contact email" className={fieldClass()} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="biz-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
          placeholder="Country" className={fieldClass()} />
        <input data-testid="biz-regnum" value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
          placeholder="Registration number" className={fieldClass()} />
      </div>
      <input data-testid="biz-logo" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
        placeholder="Logo URL (optional)" className={fieldClass()} />
    </FormShell>
  );
}
